import lexendLatin400 from "@fontsource/lexend-deca/files/lexend-deca-latin-400-normal.woff2?url";
import lexendLatin700 from "@fontsource/lexend-deca/files/lexend-deca-latin-700-normal.woff2?url";
import { rewriteHostingerLogoImagesInHtml } from "./hostingerLogo";
import {
  remoteImagesBlockedCspMeta,
  stripRemoteMailImages,
} from "./mailRemoteImages";
import { sanitizeMailHtmlParts } from "./sanitizeMailHtml";

/**
 * Default for mail that sets no typeface. The woff2 ships with the app, so the
 * computer does not need Lexend installed. `font-display: swap` paints the
 * system sans immediately, and that face stays if the file fails. Sender
 * inline styles and their own CSS still win — this is not applied to outbound
 * mail.
 */
const MAIL_READER_FONT_FACE = `
@font-face {
  font-family: "Lexend Deca";
  font-style: normal;
  font-weight: 400;
  font-display: swap;
  src: url("${lexendLatin400}") format("woff2");
  unicode-range: U+0000-00FF, U+0131, U+0152-0153, U+02BB-02BC, U+02C6, U+02DA, U+02DC, U+0304, U+0308, U+0329, U+2000-206F, U+20AC, U+2122, U+2191, U+2193, U+2212, U+2215, U+FEFF, U+FFFD;
}
@font-face {
  font-family: "Lexend Deca";
  font-style: normal;
  font-weight: 700;
  font-display: swap;
  src: url("${lexendLatin700}") format("woff2");
  unicode-range: U+0000-00FF, U+0131, U+0152-0153, U+02BB-02BC, U+02C6, U+02DA, U+02DC, U+0304, U+0308, U+0329, U+2000-206F, U+20AC, U+2122, U+2191, U+2193, U+2212, U+2215, U+FEFF, U+FFFD;
}
`;

const MAIL_READER_FONT_STACK =
  '"Lexend Deca", ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif';

/** Bare `body` so a sender stylesheet that also targets `body` replaces it. */
const MAIL_READER_FONT_DEFAULT = `
body {
  font-family: ${MAIL_READER_FONT_STACK};
  font-size: 14px;
  line-height: 1.55;
  color: #1a1a1a;
  background: #fff;
}
`;

/**
 * Email clients center a column with align="center" on a cell or table.
 * In the browser that only sets text-align, so the 600px table stays left
 * and the fluid width:100% makes the column fill the pane.
 */
const MAIL_CANVAS_ALIGN = `
.mail-body-root[align="center" i],
.mail-nested-body[align="center" i],
td[align="center" i],
th[align="center" i] {
  text-align: center;
}
.mail-body-root[align="center" i] > table,
.mail-nested-body[align="center" i] > table,
td[align="center" i] > table,
th[align="center" i] > table,
td[align="center" i] > div > table,
th[align="center" i] > div > table,
center > table,
table[align="center" i] {
  margin-left: auto;
  margin-right: auto;
}
`;

const safeCanvasColor = (value: string) => {
  const color = value.trim();
  if (
    !color ||
    color.length > 40 ||
    !/^(#[0-9a-f]{3,8}|rgba?\([^)]*\)|hsla?\([^)]*\)|[a-z]+)$/i.test(color)
  ) {
    return null;
  }
  return color;
};

/** bgcolor / background-color from the original body, so the pane matches the mail. */
const canvasColorFromRoot = (html: string) => {
  const root =
    html.match(/<div\b[^>]*\bclass="[^"]*\bmail-body-root\b[^"]*"[^>]*>/i)?.[0] ??
    "";
  const bgcolor = root.match(
    /\bbgcolor\s*=\s*(?:"([^"]*)"|'([^']*)'|([^\s>]+))/i,
  );
  const fromAttr = bgcolor?.[1] ?? bgcolor?.[2] ?? bgcolor?.[3] ?? "";
  const fromStyle =
    root.match(/background-color:\s*([^;"']+)/i)?.[1] ??
    root.match(/background:\s*([^;"']+)/i)?.[1] ??
    "";
  return safeCanvasColor(fromStyle || fromAttr);
};

export type MailIframeVariant = "reader" | "embedded";
export type MailIframeLayout = "fill" | "auto";

/** Shell chrome only — sender CSS loads after this so it wins. */
const READER_SHELL_FILL = `
  html.mail-reader-html {
    margin: 0;
    padding: 0;
    height: 100%;
    overflow: hidden;
    -webkit-text-size-adjust: 100%;
  }
  body.mail-reader-shell {
    margin: 0;
    padding: 16px 16px 32px;
    height: 100%;
    box-sizing: border-box;
    overflow-x: auto;
    overflow-y: auto;
    -webkit-overflow-scrolling: touch;
  }
  body.mail-reader-shell .mail-body-root,
  body.mail-reader-shell .mail-nested-body {
    display: block;
    box-sizing: border-box;
    width: 100%;
    max-width: 100%;
    margin: 0 auto;
    overflow: visible !important;
  }
  body.mail-reader-shell .mail-body-root img,
  body.mail-reader-shell .mail-nested-body img {
    max-width: 100%;
  }
`;

const READER_SHELL_AUTO = `
  html.mail-reader-html {
    margin: 0;
    padding: 0;
    height: auto !important;
    min-height: 0;
    -webkit-text-size-adjust: 100%;
  }
  body.mail-reader-shell {
    margin: 0;
    padding: 16px 16px 32px;
    height: auto !important;
    min-height: 0;
    overflow-x: auto;
    overflow-y: visible;
  }
  body.mail-reader-shell .mail-body-root,
  body.mail-reader-shell .mail-nested-body {
    display: block;
    box-sizing: border-box;
    width: 100%;
    max-width: 100%;
    margin: 0 auto;
    overflow: visible !important;
  }
  body.mail-reader-shell .mail-body-root img,
  body.mail-reader-shell .mail-nested-body img {
    max-width: 100%;
  }
`;

/** Restore readable spacing when senders zero out paragraph margins (Apple Mail, CRM compose). */
const READER_TYPOGRAPHY = `
body.mail-reader-shell .mail-body-root,
body.mail-reader-shell .mail-nested-body {
  word-wrap: break-word;
}
`;

/** Minimal post-sender overrides for responsive newsletter classes only. */
const READER_LAYOUT_UNLOCK = `
body.mail-reader-shell .mobile_only {
  display: none !important;
}
`;

/** Post-sender guard — keep sender width/height; only cap runaway layouts. */
const READER_MEDIA_GUARD = `
body.mail-reader-shell .mail-body-root img,
body.mail-reader-shell .mail-nested-body img,
body.mail-reader-shell .mail-body-root svg,
body.mail-reader-shell .mail-nested-body svg {
  max-width: 100% !important;
  object-fit: contain;
}
`;

const EMBEDDED_SHELL = `
  html.mail-reader-html, body.mail-reader-shell {
    margin: 0;
    padding: 0;
    height: auto !important;
    min-height: 0;
    -webkit-text-size-adjust: 100%;
  }
  body.mail-reader-shell .mail-body-root,
  body.mail-reader-shell .mail-nested-body {
    max-width: 100%;
  }
  body.mail-reader-shell .mail-body-root img,
  body.mail-reader-shell .mail-nested-body img,
  body.mail-reader-shell .mail-body-root svg,
  body.mail-reader-shell .mail-nested-body svg {
    max-width: 100% !important;
    object-fit: contain;
  }
  /* Compose quote preview — match Gmail/Apple left bar (inline styles may be diluted). */
  body.mail-reader-shell .gmail_attr {
    color: #5f6368;
    font-family: ${MAIL_READER_FONT_STACK};
    font-size: 12px;
    margin: 0 0 8px;
  }
  body.mail-reader-shell blockquote.gmail_quote,
  body.mail-reader-shell blockquote.mail-compose-quote {
    margin: 0;
    padding: 0 0 0 1ex;
    border-left: 2px solid #3474E0;
    color: #3c4043;
  }
`;

function escapeStyleText(css: string): string {
  return css.replace(/<\/style/gi, "<\\/style");
}

/** Full HTML document for sandboxed iframe email rendering. */
export function buildMailIframeSrcDoc(
  rawHtml: string,
  variant: MailIframeVariant = "reader",
  layout: MailIframeLayout = "fill",
  blockRemoteImages = false,
): string {
  const sanitized = sanitizeMailHtmlParts(
    rewriteHostingerLogoImagesInHtml(rawHtml),
  );
  const { emailStyles, headLinks } = sanitized;
  const body = blockRemoteImages
    ? stripRemoteMailImages(sanitized.body)
    : sanitized.body;
  const bodyHtml = body.includes("mail-body-root")
    ? body
    : `<div class="mail-body-root">${body}</div>`;
  const canvas = canvasColorFromRoot(body);
  const canvasCss = canvas ? `\nbody { background: ${canvas}; }\n` : "";
  const shellCss =
    MAIL_READER_FONT_FACE +
    (variant === "embedded"
      ? EMBEDDED_SHELL
      : layout === "fill"
        ? READER_SHELL_FILL
        : READER_SHELL_AUTO) +
    MAIL_CANVAS_ALIGN +
    MAIL_READER_FONT_DEFAULT +
    canvasCss;
  const senderCss = emailStyles.trim()
    ? `<style>${escapeStyleText(emailStyles)}</style>`
    : "";
  const readerUnlockCss =
    variant === "reader"
      ? `<style>${escapeStyleText(READER_LAYOUT_UNLOCK)}</style><style>${escapeStyleText(READER_TYPOGRAPHY)}</style><style>${escapeStyleText(READER_MEDIA_GUARD)}</style>`
      : "";
  const viewportMeta =
    variant === "reader"
      ? '<meta name="viewport" content="width=device-width, initial-scale=1">'
      : "";
  const imagePolicy = blockRemoteImages ? remoteImagesBlockedCspMeta() : "";
  return `<!DOCTYPE html><html lang="en" class="mail-reader-html"><head><meta charset="utf-8">${imagePolicy}${viewportMeta}<base target="_blank" rel="noopener noreferrer"><style>${shellCss}</style>${headLinks}${senderCss}${readerUnlockCss}</head><body class="mail-reader-shell">${bodyHtml}</body></html>`;
}
